import { connect } from 'react-redux'
import { rem } from 'polished'
import type { ComponentProps } from 'react'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import {
  MIN_VALID_OFFERS_TO_SHOW_RESULTS,
  MIN_INVALID_OFFERS_TO_SHOW_NO_MATCHES,
} from 'constants/naturalLanguageSearch'
import type { OfferListSortOption } from 'constants/offerListFilters'
import config from 'constants/config'

import AssistChip from 'LuxKit/Chips/AssistChip'
import BodyText from 'LuxKit/Typography/BodyText'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import TextButton from 'LuxKit/Button/TextButton'

import { addGTMEvent } from 'api/googleTagManager'
import { ANYWHERE_PLACE_ID } from '../../../constants/search'
import type { DestinationLandingPageType } from '../DestinationLandingPage/DestinationLandingPageUtils'
import type { EnabledFilters } from 'components/Search/type'

import { searchSwitchViewClick } from 'analytics/eventDefinitions'
import { selectLoggedIn } from '../../../selectors/accountSelectors'
import { useScreenSizeOnly, useScreenSizeUp } from '../../../hooks/useScreenSize'
import { trackClientEvent } from 'analytics/analytics'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import getOfferListKey from 'lib/offer/offerListKey'
import Group from '../../utils/Group'
import HotelBroadSearchLocation from './HotelSearchBroadSearchLocation'
import HotelSearchAddDestinationToggle from './HotelAddDestinationToggle/HotelSearchAddDestinationToggle'
import OfferListFilterSidebar from 'components/OfferList/OfferListFilter/OfferListFilterSidebar'
import OfferSortControl from 'components/Common/OfferSortControl/OfferSortControl'
import useGlobalSearchURLHashFilter from 'hooks/GlobalSearch/useGlobalSearchURLHashFilter'
import VerticalSpacer from '../../Common/Spacing/VerticalSpacer'

const Container = styled(CSSBreakpoint)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const MobileContainer = styled(CSSBreakpoint)`
  width: 100%;
`

const FilterAndCountContainer = styled.div`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: ${rem(16)};
  }
`

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  > * + * {
    margin-left: ${rem(8)};
  }
`

type HotelPriceMode = 'total' | 'night'

interface MappedStateProps {
  availableFilters?: App.OfferListFilterOptions
  isLoggedIn: boolean
  userId?: string
}

type InheritedOfferSortControlProps = Pick<ComponentProps<typeof OfferSortControl>, 'onSortChange'>
type InheritedOfferListFilterSidebarProps = Pick<ComponentProps<typeof OfferListFilterSidebar>, 'onFilterChange'>

interface Props extends InheritedOfferSortControlProps, InheritedOfferListFilterSidebarProps {
  mainListTotal: number
  sortOptions?: Array<OfferListSortOption>
  mapViewUrl?: string
  switchViewLabel?: string
  showPerNightPricingToggle?: boolean
  isPerNightPricing?: boolean
  onPerNightPricingChange?: (enabled: boolean) => void
  isFlexible?: boolean
  shouldShowFilters?: boolean
  filters: App.OfferListFilters
  windowSearch: string
  showOfferCount?: boolean
  showHolidayTypes?: boolean
  query?: string
  numberOfInvalidOffers?: number
  numberOfValidOffers?: number
  searchType?: string
  enabledFilters?: EnabledFilters
  fetching?: boolean
  countTextOverride?: string
  specificPlaces?: Array<App.SpecificPlace>
  destinationName?: string
  isDestinationLandingPage?: boolean
  urlLocationForDestinationLandingPage?: string
  pageTypeForDestinationLandingPage?: DestinationLandingPageType
  holidayTypeForDestinationLandingPage?: string
  showRoomsAndAmenitiesAtTop?: boolean
  showBroadSearchLocation?: boolean
}

function OfferCountAndSort(props: Props & MappedStateProps) {
  const {
    mainListTotal,
    sortOptions,
    mapViewUrl,
    switchViewLabel,
    showPerNightPricingToggle = false,
    isPerNightPricing = false,
    onPerNightPricingChange,
    isFlexible,
    shouldShowFilters = false,
    showOfferCount = true,
    query,
    numberOfInvalidOffers = undefined,
    numberOfValidOffers = undefined,
    filters,
    windowSearch,
    searchType,
    enabledFilters,
    fetching,
    countTextOverride,
    specificPlaces,
    destinationName,
    isDestinationLandingPage,
    pageTypeForDestinationLandingPage,
    holidayTypeForDestinationLandingPage,
    isLoggedIn,
    userId,
    showRoomsAndAmenitiesAtTop = false,
    showBroadSearchLocation = true,
    onFilterChange,
    onSortChange,
  } = props
  const intl = useIntl()
  const propertySearch = searchType === 'property'
  const { isFilterPanelOpen, toggleFilterPanel } = useGlobalSearchURLHashFilter()
  const isDesktopUp = useScreenSizeUp('desktop')

  const isMobile = useScreenSizeOnly('mobile')

  const filtersText = intl.formatMessage({
    id: 'pr1whn7-wNBM8zXkHG64',
    defaultMessage: 'Filters',
    description: 'Button label to open the search filters panel in the search results interface',
  })

  const mapViewText = intl.formatMessage({
    id: 'QKb19ziKOiYJ8ZBRLvOC',
    defaultMessage: 'Map view',
    description: 'Button label to switch to the map view display mode in the search results page',
  })

  const viewButtonText = switchViewLabel || mapViewText
  const totalPriceToggleText = intl.formatMessage({
    id: 'B0XZiqtnKvnF-RFSxUGy',
    defaultMessage: 'Total price',
    description: 'Segmented control option label for showing total hotel search result pricing',
  })
  const perNightToggleText = intl.formatMessage({
    id: 'bubQQ8yvjBRwpi7OVtu-',
    defaultMessage: 'Per night',
    description: 'Label shown beside the toggle that switches hotel search result prices to a per-night view',
  })

  const mobileCountText = intl.formatMessage(
    {
      id: 'iuSXeE3g-0Xo21CA_C9h',
      defaultMessage: '{count, plural, one {# offer} other {# offers}}',
      description: 'Mobile offer count on search results page in the search interface',
    },
    { count: mainListTotal },
  )
  const flexibleCountText = intl.formatMessage(
    {
      id: 'uREvpZsS5_BHa5RwBDup',
      defaultMessage: 'Showing {count, plural, one {# offer} other {# offers}} with cheapest availabilities for your dates',
      description: 'Offer count with flexible dates on search results page',
    },
    { count: mainListTotal },
  )
  const standardCountText = intl.formatMessage(
    {
      id: 'l16LF9DN7Tzpy32hOr2U',
      defaultMessage: 'Showing {count, plural, one {# offer} other {# offers}}',
      description: 'Offer count on search results page in the search and discovery interface',
    },
    { count: mainListTotal },
  )

  const countText = (() => {
    if (countTextOverride) {
      return countTextOverride
    }
    if (propertySearch) {
      return ''
    }
    if (isMobile) {
      return mobileCountText
    }
    return isFlexible ? flexibleCountText : standardCountText
  })()

  const isLuxuryEscapes = config.BRAND === 'luxuryescapes'
  const hasSortOptions = sortOptions && sortOptions?.length > 0

  const handleMapClick = useCallback(() => {
    trackClientEvent({
      subject: 'search_map_view',
      action: 'clicked',
      type: 'interaction',
      category: 'vertical_page',
    })
    addGTMEvent(searchSwitchViewClick())
  }, [])

  const selectedHotelPriceMode = useMemo<HotelPriceMode>(() => {
    return isPerNightPricing ? 'night' : 'total'
  }, [isPerNightPricing])

  const handleHotelPriceModeChange = useCallback((value: HotelPriceMode) => {
    onPerNightPricingChange?.(value === 'night')
  }, [onPerNightPricingChange])

  const hotelPriceModeToggle = showPerNightPricingToggle ? (
    <SegmentedOptionsGroup<HotelPriceMode>
      inputsName="hotelPriceMode"
      onValueChange={handleHotelPriceModeChange}
      distribution="hugged"
    >
      <SegmentedOption
        value="total"
        checked={selectedHotelPriceMode === 'total'}
        title={totalPriceToggleText}
      />
      <SegmentedOption
        value="night"
        checked={selectedHotelPriceMode === 'night'}
        title={perNightToggleText}
      />
    </SegmentedOptionsGroup>
  ) : null

  const nlsNoExactMatchesMessage = intl.formatMessage({
    id: 'C8dyVtHE5Ztueu5VhINb',
    defaultMessage: 'No exact matches, we’ve found related escapes we think you’ll love',
    description: 'Message shown on search page when no exact matches found but related offers are available',
  })
  const nlsNoExactMatchesShort = intl.formatMessage({
    id: 'MaxlZ7lg4bJoqoV_oMe7',
    defaultMessage: 'No exact matches',
    description: 'Short message shown on mobile search page when no exact matches found',
  })

  const nlsMessage = useMemo(() => {
    if (!query?.length || numberOfValidOffers === undefined || numberOfInvalidOffers === undefined) return
    if (numberOfValidOffers >= MIN_VALID_OFFERS_TO_SHOW_RESULTS) return
    if (numberOfInvalidOffers < Math.min(MIN_INVALID_OFFERS_TO_SHOW_NO_MATCHES, mainListTotal)) return

    if (isDesktopUp) {
      return nlsNoExactMatchesMessage
    }
    return nlsNoExactMatchesShort
  }, [numberOfValidOffers, numberOfInvalidOffers, mainListTotal, query, isDesktopUp, nlsNoExactMatchesMessage, nlsNoExactMatchesShort])

  const countTextWithNls = useMemo(() => {
    if (countText && nlsMessage) return `${countText} - ${nlsMessage}`
    if (countText) return countText
    if (nlsMessage) return nlsMessage
    return ''
  }, [countText, nlsMessage])

  const desktopControls = (
    <Container min="tablet">
      <FilterAndCountContainer>
        {shouldShowFilters && (
          <AssistChip
            size="medium"
            variant="filled"
            startIcon={<LineFilterAltIcon />}
            onClick={toggleFilterPanel}
          >
            {filtersText}
          </AssistChip>
        )}
        {showOfferCount && !fetching && <BodyText variant="large">{countTextWithNls}</BodyText>}
      </FilterAndCountContainer>
      <ButtonContainer>
        {hasSortOptions && (
          <OfferSortControl
            sortOptions={sortOptions}
            onSortChange={onSortChange}
          />
        )}
        {hotelPriceModeToggle}
        {!showPerNightPricingToggle && mapViewUrl && (
          <TextButton
            kind="secondary"
            variant="dark"
            to={mapViewUrl}
            startIcon={<LineMapIcon />}
            onClick={handleMapClick}
          >
            {viewButtonText}
          </TextButton>
        )}
      </ButtonContainer>
    </Container>
  )

  const offerControlsContent = (
    <div>
      <VerticalSpacer gap={16}>
        {showBroadSearchLocation && !!specificPlaces?.length && !!destinationName && (
          <HotelBroadSearchLocation
            specificPlaces={specificPlaces}
            destinationName={destinationName}
            isDestinationLandingPage={isDestinationLandingPage}
            pageTypeForDestinationLandingPage={pageTypeForDestinationLandingPage}
            holidayTypeForDestinationLandingPage={holidayTypeForDestinationLandingPage}
          />
        )}
        <MobileContainer max="mobile">
          <Group direction="vertical" gap={8}>
            <Group direction="horizontal" horizontalAlign="space-between">
              {showOfferCount && !fetching && <BodyText variant="medium">{countTextWithNls}</BodyText>}
              {isLuxuryEscapes && isLoggedIn && filters.destinationId && filters.destinationId !== ANYWHERE_PLACE_ID && userId && (
                <HotelSearchAddDestinationToggle userId={userId} placeId={filters.destinationId} destinationName={destinationName} />
              )}
            </Group>
            {hotelPriceModeToggle}
          </Group>
        </MobileContainer>
        {desktopControls}
      </VerticalSpacer>
    </div>
  )

  return (
    <>
      {offerControlsContent}
      {!!enabledFilters && (
        <OfferListFilterSidebar
          open={isFilterPanelOpen}
          onClose={toggleFilterPanel}
          filters={filters}
          sortOptions={sortOptions}
          search={windowSearch}
          enabledFilters={enabledFilters}
          onFilterChange={onFilterChange}
          showRoomsAndAmenitiesAtTop={showRoomsAndAmenitiesAtTop}
        />
      )}
    </>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState, ownProps) => {
  const offerListKey = getOfferListKey(ownProps.filters)

  return {
    availableFilters: appState.offer.offerListFilterOptions[offerListKey],
    isLoggedIn: selectLoggedIn(appState),
    userId: appState.auth.account.memberId,
  }
})(OfferCountAndSort)
